import { api } from './apiClient'


// Resolve min/max dates from period + years + quarters for use in the download filename.
// This duplicates the logic in ReportPeriodResolver intentionally —
// Content-Disposition headers are not readable by JS without Access-Control-Expose-Headers,
// which we're avoiding to keep the CORS surface minimal. This client-side resolution is
// cosmetic only (filename label); the server is authoritative for actual data filtering.
const QUARTER_START_MONTH = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 } // JS months are 0-indexed
let DATA_START_YEAR = 2016 // updated from /meta on mount; 2016 is the fallback

function resolveFilenameRange(period, years, quarters) {
  const today = new Date()
  const fmt = d => [
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
    d.getFullYear()
  ].join('.')

  const p = (period || 'LTM').toUpperCase()

  if (p === 'LTM') {
    const start = new Date(today)
    start.setFullYear(start.getFullYear() - 1)
    start.setDate(start.getDate() + 1)
    return { start: fmt(start), end: fmt(today) }
  }

  const yearList = (years && years !== '-') ? years.split(',').map(Number) : [DATA_START_YEAR, today.getFullYear()]

  if (p === 'ANNUAL') {
    const minYear = Math.min(...yearList)
    const maxYear = Math.max(...yearList)
    return {
      start: fmt(new Date(minYear, 0, 1)),
      end:   fmt(new Date(maxYear, 11, 31))
    }
  }

  if (p === 'YTD') {
    const minYear = Math.min(...yearList)
    const maxYear = Math.max(...yearList)
    const daysInMonth = new Date(maxYear, today.getMonth() + 1, 0).getDate()
    const safeDay = Math.min(today.getDate(), daysInMonth)
    return {
      start: fmt(new Date(minYear, 0, 1)),
      end:   fmt(new Date(maxYear, today.getMonth(), safeDay))
    }
  }

  if (p === 'QUARTERLY') {
    const quarterList = (quarters && quarters !== '-') ? quarters.split(',') : ['Q1', 'Q2', 'Q3', 'Q4']
    const minYear = Math.min(...yearList)
    const maxYear = Math.max(...yearList)
    const minMonth = Math.min(...quarterList.map(q => QUARTER_START_MONTH[q.toUpperCase()]))
    const maxStartMonth = Math.max(...quarterList.map(q => QUARTER_START_MONTH[q.toUpperCase()]))
    const maxEndMonth = maxStartMonth + 2
    const lastDay = new Date(maxYear, maxEndMonth + 1, 0).getDate()
    return {
      start: fmt(new Date(minYear, minMonth, 1)),
      end:   fmt(new Date(maxYear, maxEndMonth, lastDay))
    }
  }

  // Unknown period — omit date range from filename
  return null
}

export const CustomExportApi = {
  getMeta() {
    return api.get('/api/custom-export/meta')
  },

  setDataStartYear(year) {
    DATA_START_YEAR = year
  },

  /**
   * @param {string[]} selectedExports  export type keys, e.g. ['overview', 'financials']
   * @param {string[]} fundIds          selected fund GUIDs (empty = all)
   * @param {string[]} investmentIds    selected investment GUIDs (empty = all)
   * @param {string}   period           report period label, e.g. 'LTM'
   * @param {string}   years            comma-separated selected years, e.g. '2023,2024'
   * @param {string}   quarters         comma-separated selected quarters, e.g. 'Q1,Q3'
   * @param {string}   months           comma-separated selected months, e.g. 'Jan,Mar'
   * @param {string}   fields           comma-separated qualified field keys; '-' or absent = all
   */
  customExport(selectedExports, fundIds = [], investmentIds = [], period, years, quarters, months, fields) {
    const params = { exports: selectedExports.join(',') }
    if (fundIds.length)               params.fundIds       = fundIds.join(',')
    if (investmentIds.length)         params.investmentIds = investmentIds.join(',')
    if (period)                       params.period        = period
    if (years && years !== '-')       params.years         = years
    if (quarters && quarters !== '-') params.quarters      = quarters
    if (months && months !== '-') params.months          = months
    if (fields && fields !== '-') params.fields          = fields

    const periodStr = period || 'LTM'
    const range     = resolveFilenameRange(period, years, quarters)
    const created   = resolveFilenameRange('LTM', null, null).end // today as MM.DD.YYYY
    const filename  = range
      ? `BCAP_Report_${periodStr}_${range.start}_to_${range.end}_created_${created}.xlsx`
      : `BCAP_Report_${periodStr}_created_${created}.xlsx`

    return api.download('/api/custom-export/overview', filename, params)
  },

  fetchData(selectedExports, fundIds = [], investmentIds = [], period, years, quarters, months, fields) {
    const params = { exports: selectedExports.join(',') }
    if (fundIds.length)               params.fundIds       = fundIds.join(',')
    if (investmentIds.length)         params.investmentIds = investmentIds.join(',')
    if (period)                       params.period        = period
    if (years && years !== '-')       params.years         = years
    if (quarters && quarters !== '-') params.quarters      = quarters
    if (months && months !== '-') params.months          = months
    if (fields && fields !== '-') params.fields          = fields
    return api.get('/api/custom-export/data', params)
  }
}
